/**
 * StageBackdrop — full-viewport decorative tavern-wall backdrop that
 * fills the empty side gutters around the centered game canvas on wide
 * PC viewports.
 *
 * Layers (all decorative, aria-hidden, z-index 0, pointer-events none):
 *   1. Wood paneling base — warm-brown gradient + subtle vertical-plank
 *      repeating-linear-gradient at very low alpha.
 *   2. Stone trim — ~60px stone-block band at top and bottom.
 *   3. Sconces — 4 inline-SVG brass-bracket candle sconces with soft
 *      radial glow halos. Left/right walls, upper + lower. Flame flickers
 *      via CSS keyframes (skipped on prefers-reduced-motion).
 *   4. Center spotlight vignette — radial gradient pulling focus to the
 *      game canvas in the middle of the viewport.
 *   5. Dust motes — ~12 tiny golden specks drifting upward, very subtle.
 *
 * Mobile / narrow viewports hide every layer entirely (CSS media query at
 * 1100px). The component is mounted once whenever the user is in-game so
 * the sconces don't unmount between rounds.
 *
 * The whole backdrop is `position: fixed; inset: 0; z-index: 0` and
 * `pointer-events: none`, so it never affects layout, scroll, or input.
 * The existing `.play-screen` and its children stack on top (default
 * static positioning resolves above z-index 0 by source order, and
 * `#root` is `z-index: 1; isolation: isolate`).
 *
 * Single design used for every game — never per-game theming, because
 * the canvas itself is always the visual focus and a busy backdrop would
 * compete with the game's mood.
 */
export function StageBackdrop() {
  return (
    <div className="stage-backdrop" aria-hidden="true">
      <div className="stage-backdrop__wood" />
      <div className="stage-backdrop__planks" />
      <div className="stage-backdrop__stone stage-backdrop__stone--top" />
      <div className="stage-backdrop__stone stage-backdrop__stone--bottom" />

      <Sconce position="upper-left" />
      <Sconce position="lower-left" />
      <Sconce position="upper-right" />
      <Sconce position="lower-right" />

      <div className="stage-backdrop__vignette" />

      <div className="stage-backdrop__motes">
        {Array.from({ length: 12 }).map((_, i) => (
          <span
            key={i}
            className="stage-backdrop__mote"
            style={{
              // Spread motes across viewport. Pure inline math — no
              // per-frame JS, the drift is a CSS keyframe.
              left: `${(i * 83) % 100}%`,
              bottom: `${(i * 41) % 60}%`,
              animationDelay: `${(i * 1.7) % 14}s`,
              animationDuration: `${18 + ((i * 3) % 9)}s`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

function Sconce({ position }: { position: 'upper-left' | 'lower-left' | 'upper-right' | 'lower-right' }) {
  return (
    <div className={`stage-backdrop__sconce stage-backdrop__sconce--${position}`}>
      <div className="stage-backdrop__sconce-glow" />
      <svg
        className="stage-backdrop__sconce-svg"
        width="60"
        height="120"
        viewBox="0 0 60 120"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Wall plate */}
        <ellipse cx="30" cy="92" rx="14" ry="3.5" fill="#1a1108" />
        <ellipse cx="30" cy="91" rx="11" ry="2.4" fill="#3a2410" />
        {/* Bracket arm */}
        <path d="M30 92 L30 76 Q30 70 34 68 L40 66" stroke="#5a4220" strokeWidth="3" strokeLinecap="round" fill="none" />
        <path d="M30 92 L30 76 Q30 70 26 68 L20 66" stroke="#5a4220" strokeWidth="3" strokeLinecap="round" fill="none" />
        {/* Cup */}
        <ellipse cx="30" cy="62" rx="9" ry="3" fill="#2a1a0c" />
        <rect x="22" y="58" width="16" height="6" rx="1" fill="#6a4a22" />
        <rect x="22" y="58" width="16" height="2" rx="1" fill="#a8782a" />
        {/* Candle wax */}
        <rect x="26" y="44" width="8" height="16" rx="1.5" fill="#f8e6b8" />
        <rect x="26" y="44" width="8" height="3" rx="1.5" fill="#fff5d4" />
        {/* Wick */}
        <rect x="29.4" y="39" width="1.2" height="6" fill="#1a0e08" />
        {/* Flame (animated via CSS) */}
        <g className="stage-backdrop__flame">
          <path
            d="M30 14 Q24 22 24 30 Q24 38 30 40 Q36 38 36 30 Q36 22 30 14 Z"
            fill="url(#flameGrad)"
          />
          <path
            d="M30 22 Q27 28 27 33 Q27 38 30 39 Q33 38 33 33 Q33 28 30 22 Z"
            fill="#fff5c4"
            opacity="0.85"
          />
        </g>
        <defs>
          <radialGradient id="flameGrad" cx="0.5" cy="0.7" r="0.6">
            <stop offset="0%" stopColor="#fff5d4" />
            <stop offset="35%" stopColor="#ffd770" />
            <stop offset="70%" stopColor="#ff8c42" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#ff5a1f" stopOpacity="0" />
          </radialGradient>
        </defs>
      </svg>
    </div>
  );
}
